import {
  createContext,
  createElement,
  useContext,
  useState,
  type ReactNode,
} from "react";

import type { PendingCreatesSource } from "../seams";
import { createPendingCopiesStore, type PendingCopiesStore } from "./pendingCopiesStore";
import { QueuedCopiesFeed } from "./QueuedCopiesFeed";

// "The programs changed" (#380). Every usePrograms instance owns its own
// list, so a write made through one (Segui in the Library, a Modal above the
// Home that never takes its focus) left the others stale until a pull-to-
// refresh. The host makes ONE invalidator per user session and mounts it
// with ProgramsInvalidatorProvider; usePrograms subscribes when it is there
// and emits after its writes. No provider: nothing happens, as before.
//
// Same subscribe/emit shape as sessionResultsCache (Progress/), not extracted
// from it: there the subscribers sit inside a factory that also owns the
// cache and the single-flight fetch, and the shape is 10 lines.
type Channel = {
  subscribe: (fn: () => void) => () => void;
  /** Tell every subscriber the list changed. The emitter ignores its own. */
  invalidate: (except?: () => void) => void;
};

export type ProgramsInvalidator = Channel & {
  /** #475: copies a queued write promised that the network list does not
   *  show yet. One per invalidator (= per session mount); what the host's
   *  queue still carries is read back into it at mount (pendingCreates). */
  copies: PendingCopiesStore;
  /** #475: the plans lists, told when a queued plan create is delivered
   *  (the host decides when). usePlans re-reads on it. */
  plans: Channel;
};

function createChannel(): Channel {
  const subscribers = new Set<() => void>();
  return {
    subscribe: (fn) => {
      subscribers.add(fn);
      return () => {
        subscribers.delete(fn);
      };
    },
    invalidate: (except) => {
      for (const fn of [...subscribers]) if (fn !== except) fn();
    },
  };
}

export function createProgramsInvalidator(): ProgramsInvalidator {
  return { ...createChannel(), copies: createPendingCopiesStore(), plans: createChannel() };
}

const Ctx = createContext<ProgramsInvalidator | null>(null);

export function ProgramsInvalidatorProvider({
  children,
  pendingCreates,
}: {
  children: ReactNode;
  /** #475: the host's queued creates (mobile). Web passes none. */
  pendingCreates?: PendingCreatesSource;
}) {
  // One per mount: the host remounts it with the user (key / sign-out).
  const [inv] = useState(createProgramsInvalidator);
  return createElement(
    Ctx.Provider,
    { value: inv },
    pendingCreates
      ? createElement(QueuedCopiesFeed, { store: inv.copies, source: pendingCreates })
      : null,
    children,
  );
}

export const useProgramsInvalidator = (): ProgramsInvalidator | null =>
  useContext(Ctx);
